import { type SubmitHandler, useForm } from 'react-hook-form';

import type * as api from '@gitbook/api-client';
import { SITE_SECTION_TITLE_MAX_LENGTH, SITE_SECTION_TITLE_MIN_LENGTH } from '@gitbook/api-client';
import {
    Alert,
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    Fieldset,
    Form,
    Label,
    Submit,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient } from '../../../state';
import { SiteStructureItemList } from './SiteStructureItemList';
import { TitleAndIconField } from './TitleAndIconField';

type SectionFormData = {
    title: string;
    icon: string | null;
};

export interface CreateSiteSectionFormProps {
    organization: api.Organization;
    site: api.Site;
    inSiteSectionGroup?: api.SiteSectionGroup | null;
    space: api.Space;
}

export function CreateSiteSectionForm(props: CreateSiteSectionFormProps) {
    const { organization, site, inSiteSectionGroup, space } = props;
    const organizationId = organization.id;
    const form = useForm<SectionFormData>({
        defaultValues: {
            title: space.title,
            icon: null,
        },
    });

    const [isPending, startDialogAction] = useDialogAction();
    const onSubmit: SubmitHandler<SectionFormData> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                const [{ data: siteSection }] = await Promise.all([
                    apiClient.siteSections.addSectionToSite(organizationId, site.id, {
                        spaceId: space.id,
                        title: data.title,
                        icon: data.icon,
                    }),
                    apiClient.spaces.updateSpaceById(space.id, { title: data.title }).catch(() => {
                        // That's fine if the space title update fails, we still want to create the section.
                    }),
                ]);
                if (inSiteSectionGroup) {
                    await apiClient.siteSectionGroups.addSectionToGroup(
                        organizationId,
                        site.id,
                        inSiteSectionGroup.id,
                        { sectionId: siteSection.id }
                    );
                }
            }, 'Error while adding section');
            close();
        });
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset>
                    <TitleAndIconField
                        form={form}
                        titleName="title"
                        iconName="icon"
                        minLength={SITE_SECTION_TITLE_MIN_LENGTH}
                        maxLength={SITE_SECTION_TITLE_MAX_LENGTH}
                        disabled={isPending}
                        autoFocus
                    >
                        <Label>Title & Icon</Label>
                        <Description>
                            Choose a title and icon that represents this section.
                        </Description>
                    </TitleAndIconField>

                    {inSiteSectionGroup ? (
                        <SiteStructureItemList
                            description="The section will be added to this group:"
                            items={[inSiteSectionGroup]}
                        />
                    ) : null}
                </Fieldset>
                {site.published ? (
                    <Alert
                        size="small"
                        title="This section will go live right away"
                        details="Adding this section will instantly publish it to your site."
                        kind="info"
                        className="mt-6"
                    />
                ) : null}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Add section" disabled={false} />
            </DialogFooter>
        </Form>
    );
}
